import React, { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Fase 19/22 · Banner de modo simulado con auto-ocultado + textos honestos por
 * plataforma:
 *   - IG/FB: "Modo simulado" — se oculta cuando composio está activo.
 *   - TikTok: "Subida manual asistida" — SIEMPRE visible (la API de TikTok no
 *     está disponible vía Composio; el flujo es copiar+descargar y subir a mano).
 *   - Marketplace/MercadoLibre: "Modo asistido" — borrador listo para copiar.
 *
 * Mapa a claves del /mode/summary del backend.
 */
const PLATFORM_TO_MODE_KEY = {
  instagram: "composio",
  facebook: "composio",
  tiktok: "composio",
  whatsapp: "whatsapp",
  email: "resend",
};

const LABELS = {
  tiktok: {
    compact: "Subida manual asistida",
    title: "Subida manual asistida.",
    body: "TikTok no expone API oficial de publicación en Composio. Genera el contenido aquí; luego usa los botones de copiar y descargar para subir manualmente desde la app. Es el flujo oficial recomendado.",
    color: "gold",
  },
  marketplace: {
    compact: "Modo asistido",
    title: "Modo asistido.",
    body: "Facebook Marketplace no expone API pública de publicación. Genera y aprueba el listing aquí; luego copia el texto y descarga las fotos para pegar en Marketplace en pocos segundos.",
    color: "gold",
  },
  whatsapp: {
    compact: "WhatsApp Business Cloud API",
    title: "Este módulo usa WhatsApp Business Cloud API.",
    body: "El código QR de WhatsApp Web NO está disponible por la API oficial (no lo expone Composio). Los mensajes se envían vía token + phone_number_id aislado por empresa. Verifica la conexión en Conectividad.",
    color: "gold",
  },
  default: {
    compact: "Modo simulado",
    title: "Modo simulado.",
    body: "Estas conexiones y publicaciones no llegan a redes reales. La arquitectura ya está lista (provider/adapter) para activar publicación real con Composio / Meta Graph API sin cambiar código de UI.",
    color: "gold",
  },
};

export default function SimulatedBanner({ compact = false, testid = "simulated-banner", platform = null }) {
  const [hidden, setHidden] = useState(false);
  const isAssistedOnly = platform === "tiktok" || platform === "marketplace" || platform === "whatsapp";

  useEffect(() => {
    if (isAssistedOnly) return; // siempre visible
    const modeKey = PLATFORM_TO_MODE_KEY[platform];
    if (!modeKey) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/integrations/mode/summary");
        if (alive && data && data[modeKey]) setHidden(true);
      } catch { /* keep visible on error */ }
    })();
    return () => { alive = false; };
  }, [platform, isAssistedOnly]);

  if (hidden) return null;

  const meta = LABELS[platform] || LABELS.default;

  if (compact) {
    return (
      <span
        data-testid={testid}
        className="inline-flex items-center gap-1 rounded-full bg-brand-gold-soft border border-brand-gold/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-gold-dark"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-brand-gold-dark" />
        {meta.compact}
      </span>
    );
  }
  return (
    <div
      data-testid={testid}
      className="flex items-start gap-3 rounded-lg border border-brand-gold/40 bg-brand-gold-soft/50 px-4 py-3"
    >
      <AlertCircle className="h-5 w-5 text-brand-gold-dark shrink-0 mt-0.5" />
      <div className="text-sm text-stone-700 leading-relaxed">
        <strong className="font-semibold text-brand-gold-dark">{meta.title}</strong>{" "}
        {meta.body}
      </div>
    </div>
  );
}
